import type { OperationCategoryEnum, OperationCategoryEnumFR } from './enums/OperationCategoryEnum';
import type {
	OperationFinancialInstrumentEnum,
	OperationFinancialInstrumentEnumFR
} from './enums/OperationFinancialInstrumentEnum';
import type { OperationStatusEnum, OperationStatusEnumFR } from './enums/OperationStatusEnum';
import type { PartnershipLabelEnum, PartnershipLabelEnumFR } from './enums/PartnershipLabelEnum';
import type { TargetTypeEnumFR, TargetTypeEnum } from './enums/TargetTypeEnum';
import type { DistrictEnum } from './enums/DistrictEnum';
import type { CountyEnum } from './enums/CountyEnum';
import { getOperationCategoryFromFR } from './enums/OperationCategoryEnum';
import { getTargetTypeFromFR } from './enums/TargetTypeEnum';
import { getOperationFinancialInstrumentFromFR } from './enums/OperationFinancialInstrumentEnum';
import { getOperationStatusFromFr } from './enums/OperationStatusEnum';
import { getPartnershipLabelFromFR } from './enums/PartnershipLabelEnum';
import type { SvelteComponent } from 'svelte';

export type OperationSummaryModel = {
	id: number;
	name: string;
	interventionModel: string;
	category?: OperationCategoryEnum;
	sector: string;
	type: TargetTypeEnum;
	amountToSeek: number;
	amountNeededMax: number;
	amountNeededMin: number;
	amountReachedExcludingWiseed: number;
	fundRaisingStartDate: string;
	fundRaisingEndDate: string;
	voteEndDate: Date;
	amountSubscribed: number;
	isOngoingClosing: boolean;
	isVoteOngoingAudit: boolean;
	operationTrancheOpeningDate: Date;
	operationTrancheStatus: string;
	operationTrancheAgeDate: Date;
	operationTrancheAgeYear: number;
	operationDissolveDate: Date;
	operationTrancheDissolveYear: number;
	financialInstrument: OperationFinancialInstrumentEnum;
	minCoupon: number;
	maxCoupon: number;
	financialInstrumentInTarget: string;
	isPaymentTransferPEA: boolean;
	isTaxReliefISF: boolean;
	isTaxReliefIRPP: boolean;
	rateOfTaxExemptionIRPP: number;
	isTaxReliefIntakeDisposal: boolean;
	percentageYieldBond: number;
	operationClosingProject: string;
	outputCoefficient: number;
	messageOutputFr: string;
	projectId: number;
	projectFirstReviewSlugifyName: string;
	projectTypeTarget: string;
	projectDistrict: DistrictEnum;
	projectCounty: CountyEnum;
	isProjectInPreview: boolean;
	projectName: string;
	projectCity: string;
	projectPostalCode: string;
	managerName: string;
	projectShortDescriptionFr: string;
	projectShortCoverImageURL: string;
	projectShortImageCardURL: string;
	projectLogoURL: string;
	projectNbFavorites: number;
	projectNbAssociates: number;
	projectNbActiveComments: number;
	nbVotes: number;
	averageVotes: number;
	vote: number;
	follows: boolean;
	isSessionVoteOpen: boolean;
	hasTrancheInFinancing: boolean;
	hasTrancheInClosing: boolean;
	hasTrancheInGPC: boolean;
	isOperationOut: boolean;
	status: OperationStatusEnum;
	partnershipLabels?: PartnershipLabelEnum;
	isConfidential: boolean;
	isPrivateAccess: boolean;
	isPremiumOnly: boolean;
	amountCollected: number;
	nbOfInvestors: number;
	percentageCollected: number;
	outingsHorizon: number;
	svelteComp: SvelteComponent | null;
};

export type OperationSummaryResponse = {
	id: number;
	nom: string;
	modeleIntervention: string;
	categorie: OperationCategoryEnumFR;
	secteur: string;
	type: TargetTypeEnumFR;
	montantRecherche: number;
	montantRequis: number;
	montantMaximumRecherche: number;
	montantAtteintHorsWiseed: number;
	dateDebutCollecte: string;
	dateFinCollecte: string;
	dateFinVote: Date;
	montantInvesti: number;
	clotureEnCours: boolean;
	operationTrancheDateOuvertureOperation: Date;
	operationTrancheStatut: string;
	operationTrancheDateAge: Date;
	operationTrancheAnneeAge: number;
	operationDateDissolution: Date;
	operationTrancheAnneeDissolution: number;
	instrumentFinancier: OperationFinancialInstrumentEnumFR;
	ticketMini: number;
	ticketMax: number;
	instrumentFinancierDansLaCible: string;
	paiementVirementPEA: boolean;
	defiscISF: boolean;
	defiscIRPP: boolean;
	tauxDefiscIRPP: number;
	defiscApportCession: boolean;
	pourcentRendementEmpruntObligataire: number;
	operationSortieProjet: string;
	coefficientSortie: number;
	messageSortieFr: string;
	projetId: number;
	projetNomPremiereRevisionSlugified: string;
	projetTypeCible: string;
	projetRegion: DistrictEnum;
	projetDepartement: CountyEnum;
	projetEnPreview: boolean;
	projetNom: string;
	projetVille: string;
	projetCodePostal: string;
	gestionnaireNom: string;
	projetDescriptionCourteFr: string;
	projetImageCoverSmallUrl: string;
	projetImageCardSmallUrl: string;
	projetLogoUrl: string;
	projetNbFavoris: number;
	projetNbAssocies: number;
	projetNbCommentairesActifs: number;
	sessionVoteAffichageFrontAuditEnCours: boolean;
	nbVotes: number;
	moyenneVotes: number;
	vote: number;
	follows: boolean;
	sessionVoteOuverte: boolean;
	hasTrancheEnFinancement: boolean;
	hasTrancheEnClosing: boolean;
	hasTrancheEnGpc: boolean;
	operationIsSortie: boolean;
	statut: OperationStatusEnumFR;
	tagPartenariat: PartnershipLabelEnumFR;
	confidentielle: boolean;
	accesPrive: boolean;
	accesPremiumUniquement: boolean;
	montantCollecte: number;
	investisseurs: number;
	pourcentageCollecte: number;
	horizonSortie: number;
};

export const translateOperationSummaryResponse = (
	o: OperationSummaryResponse
): OperationSummaryModel => {
	return {
		id: o.id,
		name: o.nom,
		interventionModel: o.modeleIntervention,
		category: getOperationCategoryFromFR(o.categorie),
		sector: o.secteur,
		type: getTargetTypeFromFR(o.type),
		amountToSeek: o.montantRecherche,
		amountNeededMin: o.montantRequis,
		amountNeededMax: o.montantMaximumRecherche,
		amountReachedExcludingWiseed: o.montantAtteintHorsWiseed,
		fundRaisingStartDate: o.dateDebutCollecte,
		fundRaisingEndDate: o.dateFinCollecte,
		amountSubscribed: o.montantInvesti,
		isOngoingClosing: o.clotureEnCours,
		operationTrancheOpeningDate: o.operationTrancheDateOuvertureOperation,
		operationTrancheStatus: o.operationTrancheStatut,
		operationTrancheAgeDate: o.operationTrancheDateAge,
		operationTrancheAgeYear: o.operationTrancheAnneeAge,
		operationDissolveDate: o.operationDateDissolution,
		operationTrancheDissolveYear: o.operationTrancheAnneeDissolution,
		financialInstrument: getOperationFinancialInstrumentFromFR(o.instrumentFinancier),
		minCoupon: o.ticketMini,
		maxCoupon: o.ticketMax,
		financialInstrumentInTarget: o.instrumentFinancierDansLaCible,
		isPaymentTransferPEA: o.paiementVirementPEA,
		isTaxReliefISF: o.defiscISF,
		isTaxReliefIRPP: o.defiscIRPP,
		rateOfTaxExemptionIRPP: o.tauxDefiscIRPP,
		isTaxReliefIntakeDisposal: o.defiscApportCession,
		percentageYieldBond: o.pourcentRendementEmpruntObligataire,
		operationClosingProject: o.operationSortieProjet,
		outputCoefficient: o.coefficientSortie,
		messageOutputFr: o.messageSortieFr,
		projectId: o.projetId,
		projectFirstReviewSlugifyName: o.projetNomPremiereRevisionSlugified,
		projectTypeTarget: o.projetTypeCible,
		projectDistrict: o.projetRegion,
		projectCounty: o.projetDepartement,
		isProjectInPreview: o.projetEnPreview,
		projectName: o.projetNom,
		projectCity: o.projetVille,
		projectPostalCode: o.projetCodePostal,
		managerName: o.gestionnaireNom,
		projectShortDescriptionFr: o.projetDescriptionCourteFr,
		projectShortCoverImageURL: o.projetImageCoverSmallUrl,
		projectShortImageCardURL: o.projetImageCardSmallUrl,
		projectLogoURL: o.projetLogoUrl,
		projectNbFavorites: o.projetNbFavoris,
		projectNbAssociates: o.projetNbAssocies,
		projectNbActiveComments: o.projetNbCommentairesActifs,
		voteEndDate: o.dateFinVote,
		isVoteOngoingAudit: o.sessionVoteAffichageFrontAuditEnCours,
		nbVotes: o.nbVotes,
		averageVotes: o.moyenneVotes,
		isSessionVoteOpen: o.sessionVoteOuverte,
		hasTrancheInFinancing: o.hasTrancheEnFinancement,
		hasTrancheInClosing: o.hasTrancheEnClosing,
		hasTrancheInGPC: o.hasTrancheEnGpc,
		isOperationOut: o.operationIsSortie,
		status: getOperationStatusFromFr(o.statut),
		vote: o.vote,
		follows: o.follows,
		partnershipLabels: getPartnershipLabelFromFR(o.tagPartenariat),
		isConfidential: o.confidentielle,
		isPrivateAccess: o.accesPrive,
		isPremiumOnly: o.accesPremiumUniquement,
		amountCollected: o.montantCollecte,
		nbOfInvestors: o.investisseurs,
		percentageCollected: o.pourcentageCollecte,
		outingsHorizon: o.horizonSortie,
		svelteComp: null
	};
};

export const translateOperationSummaryResponseArray = (
	o?: OperationSummaryResponse[]
): OperationSummaryModel[] => {
	return o?.map((e) => translateOperationSummaryResponse(e)) ?? [];
};
